import { Component } from '@theme/component';

/**
 * `<backdrop-blend-text>` — text whose fill is a filtered view of whatever is painted behind it
 * (image, VIDEO, canvas or any GPU-composited layer), on every engine.
 *
 * WHY NOT `mix-blend-mode`: WebKit (all iOS browsers + macOS Safari) only evaluates blend modes
 * in software, when the backdrop is painted into the same backing store as the text. When the
 * backdrop is a composited layer (`<video>`, `<canvas>`, anything with `will-change`/3D transform)
 * the compositor does NOT blend: the text layer sees an empty backdrop, so white text with
 * `overlay` + `difference` collapses to solid black. `backdrop-filter`, by contrast, IS applied
 * by the compositor and can read those layers — so instead of blending glyphs we paint a
 * backdrop-filtered layer and clip it to the shape of the glyphs.
 *
 * HOW: the real, accessible text stays in the DOM (server-rendered; made transparent once the
 * effect is active). The element measures the rendered lines of that text (same font, same
 * positions — read from the live layout, so wrapping always matches), mirrors them as white
 * `<text>` inside an inline SVG `<mask>`, and overlays ONE `.fx` layer with
 * `backdrop-filter: <data-filter>` + `mask: url(#…)`. Zero per-frame JS: the GPU does the work.
 * It re-measures on resize, webfont load and Section Rendering API updates.
 *
 * Why an SVG `<mask>` and not `clip-path: url(#clipPath)`: Blink drops the clip-path of a
 * backdrop-filter element whenever an ancestor clips with `border-radius` + `overflow: hidden`
 * (every section with a corner radius) and paints the whole filtered box; masks survive that.
 * Gate: unprefixed `mask: url(#…)` (Chrome 120+, Safari 15.4+, Firefox) — older engines simply
 * keep the plain text.
 *
 * Attributes:
 *  - `data-filter`: backdrop-filter chain. `brightness(2) invert(1)` ≡ overlay(white) → difference(white).
 *  - `data-fill`:   optional color composited over the filtered backdrop (e.g. `rgba(255,255,255,.24)`).
 *  - `--backdrop-blend-bleed` (CSS): how far the fx layer extends past the box so glyph overshoot
 *    (tight line-height, descenders) is never cut. Default 24px.
 *
 * @example
 *   <backdrop-blend-text data-filter="brightness(2) invert(1)">
 *     <h1>Título</h1><p>Subtítulo</p>
 *   </backdrop-blend-text>
 *
 * @extends Component
 */

const TAG = 'backdrop-blend-text';
const SVG_NS = 'http://www.w3.org/2000/svg';

/** Everything the effect needs; otherwise the server-rendered text is left as is. */
const SUPPORTED =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  (CSS.supports('backdrop-filter', 'invert(1)') || CSS.supports('-webkit-backdrop-filter', 'invert(1)')) &&
  CSS.supports('mask', 'url(#a)') && // unprefixed shorthand ⇒ SVG <mask> references work on HTML
  typeof document.createRange === 'function' &&
  typeof ResizeObserver === 'function';

/** Typography longhands copied from the live text onto each SVG <text>, so glyphs and advances match. */
const FONT_PROPS = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-stretch',
  'font-variant',
  'font-feature-settings',
  'font-variation-settings',
  'font-kerning',
  'font-optical-sizing',
  'letter-spacing',
  'word-spacing',
];

/* Base styles, injected once — the element must work in any section without extra CSS. */
const STYLE_ID = `${TAG}-styles`;
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    ${TAG} { display: block; position: relative; }
    ${TAG}[data-fx='on'] { -webkit-text-fill-color: transparent; }
    ${TAG} > .${TAG}__svg { position: absolute; width: 0; height: 0; overflow: hidden; }
    ${TAG} > .${TAG}__fx {
      position: absolute;
      inset: calc(-1 * var(--backdrop-blend-bleed, 24px));
      pointer-events: none;
    }
  `;
  document.head.append(style);
}

let uid = 0;

/**
 * @param {CSSStyleDeclaration} cs
 * @returns {string} inline CSS with the typography longhands of `cs`
 */
function fontCss(cs) {
  return FONT_PROPS.map((prop) => `${prop}:${cs.getPropertyValue(prop)};`).join('');
}

/**
 * Applies `text-transform` in JS so the SVG mirror never depends on engine support for it.
 * @param {string} text
 * @param {string} transform computed `text-transform`
 */
function transformCase(text, transform) {
  const lang = document.documentElement.lang || undefined;
  switch (transform) {
    case 'uppercase':
      return text.toLocaleUpperCase(lang);
    case 'lowercase':
      return text.toLocaleLowerCase(lang);
    case 'capitalize':
      return text.replace(/(^|\s)(\S)/g, (_, space, char) => space + char.toLocaleUpperCase(lang));
    default:
      return text;
  }
}

/**
 * @typedef {Object} Line
 * @property {number} x        left of the first glyph, relative to the fx layer
 * @property {number} y        alphabetic baseline, relative to the fx layer
 * @property {string} text     the characters rendered on this line
 * @property {CSSStyleDeclaration} cs computed style of the text's parent element
 */

class BackdropBlendText extends Component {
  /** @type {SVGSVGElement | undefined} */
  #svg;
  /** @type {SVGMaskElement | undefined} */
  #mask;
  /** @type {HTMLElement | undefined} */
  #fx;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {MutationObserver | undefined} */
  #textObserver;
  /**
   * Every node this element creates (layers, mask text, probes) — by identity, so nodes already
   * detached by a rebuild are still recognised as ours and never re-trigger a build.
   * @type {WeakSet<Node>}
   */
  #owned = new WeakSet();
  #id = `${TAG}-${++uid}`;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    if (!SUPPORTED) return;

    this.#schedule();
    this.#resizeObserver = new ResizeObserver(this.#schedule);
    this.#resizeObserver.observe(this);
    // Text edited in place (Theme Editor morphs) with no size change — still needs a new mirror.
    this.#textObserver = new MutationObserver((mutations) => {
      const touchesContent = mutations.some((mutation) =>
        mutation.type === 'characterData'
          ? this.#isContent(mutation.target)
          : [...mutation.addedNodes, ...mutation.removedNodes].some((node) => this.#isContent(node))
      );
      if (touchesContent) this.#schedule();
    });
    this.#textObserver.observe(this, { childList: true, characterData: true, subtree: true });
    // Webfonts land after first paint (`font-display: swap`) — glyph metrics change, so re-mirror.
    document.fonts?.addEventListener('loadingdone', this.#schedule);
    document.fonts?.ready.then(this.#schedule);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#textObserver?.disconnect();
    document.fonts?.removeEventListener('loadingdone', this.#schedule);
    cancelAnimationFrame(this.#frame);
  }

  /** Section Rendering API re-render (Theme Editor edits) — text may have changed. */
  updatedCallback() {
    super.updatedCallback();
    if (SUPPORTED) this.#schedule();
  }

  /** Coalesces rebuild requests (resize, font load, re-render) into one per frame. */
  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#build());
  };

  /** Rebuilds the mask from the current layout: one batch of reads, then one batch of writes. */
  #build() {
    if (!this.isConnected) return;
    this.#ensureLayers();
    const fx = this.#fx;
    const mask = this.#mask;
    if (!fx || !mask) return;

    const nodes = this.#textNodes();

    // One probe per distinct parent element gives the baseline offset for that font + line-height.
    /** @type {Map<Element, { cs: CSSStyleDeclaration, probe: ReturnType<typeof createProbe>, offset: number }>} */
    const parents = new Map();
    for (const node of nodes) {
      const parent = node.parentElement;
      if (!parent || parents.has(parent)) continue;
      const cs = getComputedStyle(parent);
      const probe = createProbe(cs);
      this.#owned.add(probe.wrapper);
      parents.set(parent, { cs, probe, offset: 0 });
    }

    // Writes: mount probes. Reads: everything, in one go (a single forced layout).
    for (const { probe } of parents.values()) this.append(probe.wrapper);
    const fxRect = fx.getBoundingClientRect();
    for (const entry of parents.values()) entry.offset = entry.probe.baselineOffset();

    /** @type {Line[]} */
    const lines = [];
    for (const node of nodes) {
      const entry = node.parentElement && parents.get(node.parentElement);
      if (entry) lines.push(...linesOf(node, entry.cs, entry.offset, fxRect));
    }

    // Writes: unmount probes, mirror the lines into the mask, hide the DOM text.
    for (const { probe } of parents.values()) probe.wrapper.remove();
    mask.setAttribute('width', String(Math.ceil(fxRect.width)));
    mask.setAttribute('height', String(Math.ceil(fxRect.height)));
    const mirrors = lines.map(textElement);
    for (const mirror of mirrors) this.#owned.add(mirror);
    mask.replaceChildren(...mirrors);
    this.dataset.fx = lines.length ? 'on' : 'off';
  }

  /** Creates the SVG mask and the fx layer once (re-created if a DOM morph dropped them). */
  #ensureLayers() {
    if (!this.#svg?.isConnected) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', `${TAG}__svg`);
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      const mask = document.createElementNS(SVG_NS, 'mask');
      mask.id = this.#id;
      // User space = the fx layer's border box; the region is sized on every build.
      mask.setAttribute('maskUnits', 'userSpaceOnUse');
      mask.setAttribute('maskContentUnits', 'userSpaceOnUse');
      mask.setAttribute('mask-type', 'luminance');
      mask.setAttribute('x', '0');
      mask.setAttribute('y', '0');
      svg.append(mask);
      this.#svg = svg;
      this.#mask = mask;
      this.#owned.add(svg);
      this.append(svg);
    }

    if (!this.#fx?.isConnected) {
      const fx = document.createElement('span');
      fx.className = `${TAG}__fx`;
      fx.setAttribute('aria-hidden', 'true');
      const filter = this.dataset.filter || 'invert(1)';
      const fill = this.dataset.fill;
      fx.style.cssText =
        `-webkit-backdrop-filter:${filter};backdrop-filter:${filter};` +
        `-webkit-mask:url(#${this.#id});mask:url(#${this.#id});` +
        (fill ? `background:${fill};` : '');
      this.#fx = fx;
      this.#owned.add(fx);
      this.append(fx); // last child → painted above the (transparent) text
    }
  }

  /**
   * Whether a node belongs to the merchant's content, as opposed to the layers, mask text and
   * probes this element adds (and removes) itself — those must never trigger a rebuild, or it
   * would loop once per frame.
   * @param {Node | null} node
   */
  #isContent(node) {
    for (let current = node; current; current = current.parentNode) {
      if (this.#owned.has(current)) return false;
    }
    return true;
  }

  /** The visible text nodes of the content — never our own SVG/fx layers. */
  #textNodes() {
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, (node) => {
      if (!node.parentElement || !this.#isContent(node)) return NodeFilter.FILTER_REJECT;
      return /\S/.test(/** @type {Text} */ (node).data) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    });
    /** @type {Text[]} */
    const nodes = [];
    while (walker.nextNode()) nodes.push(/** @type {Text} */ (walker.currentNode));
    return nodes;
  }
}

/**
 * Hidden replica of an element's typography with a 0×0 inline-block sitting on the baseline:
 * `marker.bottom − text.top` is the baseline offset that Range rects need for that font.
 * Kept out of the live text so it can never change the wrapping it measures.
 * @param {CSSStyleDeclaration} cs
 */
function createProbe(cs) {
  const wrapper = document.createElement('span');
  wrapper.setAttribute('aria-hidden', 'true');
  wrapper.style.cssText =
    'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;white-space:nowrap;' +
    fontCss(cs) +
    `line-height:${cs.lineHeight};`;
  const marker = document.createElement('span');
  marker.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline;';
  const sample = document.createTextNode('Hg');
  wrapper.append(marker, sample);

  return {
    wrapper,
    baselineOffset() {
      const range = document.createRange();
      range.selectNodeContents(sample);
      return marker.getBoundingClientRect().bottom - range.getBoundingClientRect().top;
    },
  };
}

/**
 * Splits a text node into its rendered lines by measuring each character's rect: characters that
 * share a top edge are on the same line. Word-level measuring would miss long words that wrap.
 * @param {Text} node
 * @param {CSSStyleDeclaration} cs
 * @param {number} baselineOffset
 * @param {DOMRect} fxRect
 * @returns {Line[]}
 */
function linesOf(node, cs, baselineOffset, fxRect) {
  const range = document.createRange();
  /** @type {{ top: number, left: number, text: string }[]} */
  const raw = [];
  let line = null;
  let index = 0;

  for (const char of node.data) {
    const start = index;
    index += char.length; // code points, so surrogate pairs are never split
    const blank = /\s/.test(char);
    range.setStart(node, start);
    range.setEnd(node, index);
    const rect = range.getClientRects()[0];
    if (!rect || (blank && rect.width === 0)) continue; // collapsed whitespace

    if (!line || Math.abs(rect.top - line.top) > 1) {
      line = { top: rect.top, left: Infinity, text: '' };
      raw.push(line);
    }
    if (!blank) line.left = Math.min(line.left, rect.left);
    line.text += char;
  }

  return raw
    .filter((entry) => entry.text.trim() && Number.isFinite(entry.left))
    .map((entry) => ({
      x: entry.left - fxRect.left,
      y: entry.top + baselineOffset - fxRect.top,
      text: entry.text.replace(/\s+/g, ' ').trim(),
      cs,
    }));
}

/**
 * @param {Line} line
 * @returns {SVGTextElement}
 */
function textElement(line) {
  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('x', line.x.toFixed(2));
  text.setAttribute('y', line.y.toFixed(2));
  // Luminance mask: white = visible. Explicit fill so it never inherits the host's transparent text.
  text.style.cssText = `${fontCss(line.cs)}text-transform:none;fill:#fff;-webkit-text-fill-color:#fff;`;
  text.textContent = transformCase(line.text, line.cs.textTransform);
  return text;
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, BackdropBlendText);
}
